import { Icon, Text } from '@vinted/web-ui'
import { ExclamationPoint24, Flag24, LightBulb24 } from '@vinted/monochrome-icons'

type Conversation = {
  messages: Array<string>
  avatarImageSrc: string
}

type BulletPoint = {
  text: string
  icon: React.ComponentProps<typeof Icon>['name']
}

type Slide = {
  title: string
  body: string
  image: string
  sectionTitle?: string
  step1?: string
  step2?: string
  step3?: string
  helpPrompt?: string
  cta?: string
  conversations?: Array<Conversation>
  bulletPoints?: Array<BulletPoint>
  reportImages?: Array<string>
  report?: string
}

type ABTestConfig = {
  introSlide: Slide
  carouselSlides: Array<Slide>
  buttonTranslations: {
    intro: string
    close: string
  }
  showConversationLabels: boolean
  showConversationBackground: boolean
  titleType: React.ComponentProps<typeof Text>['type']
  showIntroSpacer: boolean
  showDividerOnEducationOnly: boolean
  progressBarDuration: number
  exampleDurations: Record<number, Array<number>>
}

export const MANDATORY_INTRO_SLIDE_TIME = 2000

export const AB_TEST_VARIANTS: Record<'old' | 'new', ABTestConfig> = {
  old: {
    introSlide: {
      title: 'intro_screen.header',
      body: 'intro_screen.body',
      image: 'letter-vinted-app.svg',
      bulletPoints: [
        {
          text: 'intro_screen.bullet_1',
          icon: ExclamationPoint24,
        },
        {
          text: 'intro_screen.bullet_2',
          icon: LightBulb24,
        },
        {
          text: 'intro_screen.bullet_3',
          icon: Flag24,
        },
      ],
    },
    carouselSlides: [
      {
        title: 'education_screen_1.header',
        body: 'education_screen_1.body',
        image: 'phone-lock.svg',
        sectionTitle: 'education_screen_1.section_title',
        conversations: [
          {
            messages: ['education_screen_1.example_1_new'],
            avatarImageSrc: 'avatar-1.png',
          },
          {
            messages: ['education_screen_1.example_2'],
            avatarImageSrc: 'avatar-2.png',
          },
          {
            messages: ['education_screen_1.example_3'],
            avatarImageSrc: 'avatar-3.png',
          },
          {
            messages: ['education_screen_1.example_4'],
            avatarImageSrc: 'avatar-4.png',
          },
        ],
      },
      {
        title: 'education_screen_2.header',
        body: 'education_screen_2.body',
        image: 'phone-hands.svg',
        sectionTitle: 'education_screen_1.section_title',
        conversations: [
          {
            messages: ['education_screen_2.example_1'],
            avatarImageSrc: 'avatar-4.png',
          },
          {
            messages: ['education_screen_2.example_2', 'qr-code.png'],
            avatarImageSrc: 'avatar-5.png',
          },
          {
            messages: ['education_screen_2.example_3_new'],
            avatarImageSrc: 'avatar-6.png',
          },
        ],
      },
      {
        title: 'education_screen_3.header',
        body: 'education_screen_3.body',
        image: 'megaphone-messages.svg',
        step1: 'education_screen_3.step_1',
        step2: 'education_screen_3.step_2',
        step3: 'education_screen_3.step_3',
        sectionTitle: 'education_screen_3.section_title',
        helpPrompt: 'education_screen_3.help_prompt_new',
        cta: 'education_screen_3.cta',
      },
    ],
    buttonTranslations: {
      intro: 'intro_screen.cta',
      close: 'education_screen_3.cta',
    },
    showConversationLabels: false,
    showConversationBackground: true,
    titleType: 'heading-xl',
    showIntroSpacer: true,
    showDividerOnEducationOnly: true,
    progressBarDuration: 5000,
    exampleDurations: {
      0: [5000, 2500, 2500, 3000],
      1: [5000, 2000, 4000],
      2: [3000, 2000],
    },
  },
  new: {
    introSlide: {
      title: 'intro_screen.header',
      body: 'intro_screen.body',
      image: 'letter-vinted-app.svg',
      bulletPoints: [
        {
          text: 'intro_screen.bullet_1',
          icon: ExclamationPoint24,
        },
        {
          text: 'intro_screen.bullet_2',
          icon: LightBulb24,
        },
        {
          text: 'intro_screen.bullet_3',
          icon: Flag24,
        },
      ],
    },
    carouselSlides: [
      {
        title: 'education_screen_1.header',
        body: 'education_screen_1.body',
        image: 'phone-lock.svg',
        sectionTitle: 'education_screen_1.section_title_2',
        conversations: [
          {
            messages: ['education_screen_1.example_1_new'],
            avatarImageSrc: 'avatar-1.png',
          },
          {
            messages: ['education_screen_1.example_2'],
            avatarImageSrc: 'avatar-2.png',
          },
          {
            messages: ['education_screen_1.example_3'],
            avatarImageSrc: 'avatar-3.png',
          },
          {
            messages: ['education_screen_1.example_4'],
            avatarImageSrc: 'avatar-4.png',
          },
        ],
      },
      {
        title: 'education_screen_2.header',
        body: 'education_screen_2.body',
        image: 'phone-hands.svg',
        sectionTitle: 'education_screen_1.section_title_2',
        conversations: [
          {
            messages: ['education_screen_2.example_1'],
            avatarImageSrc: 'avatar-4.png',
          },
          {
            messages: ['education_screen_2.example_2', 'qr-code.png'],
            avatarImageSrc: 'avatar-5.png',
          },
          {
            messages: ['education_screen_2.example_3_new'],
            avatarImageSrc: 'avatar-6.png',
          },
        ],
      },
      {
        title: 'education_screen_3.header',
        body: 'education_screen_3.body',
        image: 'megaphone-messages.svg',
        step1: 'education_screen_3.step_1',
        step2: 'education_screen_3.step_2',
        step3: 'education_screen_3.step_3',
        sectionTitle: 'education_screen_3.section_title',
        helpPrompt: 'education_screen_3.help_prompt_new',
        cta: 'education_screen_3.cta',
        reportImages: ['report-1.png', 'report-2.png'],
        report: 'education_screen_3.report',
      },
    ],
    buttonTranslations: {
      intro: 'intro_screen.cta',
      close: 'education_screen_3.cta',
    },
    showConversationLabels: false,
    showConversationBackground: false,
    titleType: 'heading-xl',
    showIntroSpacer: true,
    showDividerOnEducationOnly: true,
    progressBarDuration: 4000,
    exampleDurations: {
      0: [5000, 2500, 2500, 3000],
      1: [5000, 2000, 4000],
      2: [3000, 2000],
    },
  },
} as const

export type { Conversation, BulletPoint, Slide, ABTestConfig }
